import { add as snackbarAdd } from "actions/snackbars";
import moment from "moment";
import { createRecord as createItemRecord } from "records/item";
import { CALL_API } from "constants/api";
import {
    getInventoryForWarehouse,
    getItemAvailability,
    getItemVariants,
    getItemWarehouseReplenishments,
} from "queries/items";
import { ItemSchema } from "schemas/items/item";
import { checkForLogout, requestAsync } from "lib/api";
import { formatMessage } from "lib/globalize-util";
import { push } from "react-router-redux";
import { Location } from "history";
import { resetCurrentSelection } from "actions/customization-actions";

import {
    ITEM_FAILURE,
    ITEM_INVENTORY,
    ITEM_REQUEST,
    ITEM_SET_CURRENT,
    ITEM_SUCCESS,
    ITEM_VARIENTS,
    ITEM_WAREHOUSE_AVAILABILITY,
    ITEM_WAREHOUSE_CHANGE,
    ITEM_WAREHOUSE_REPLENISHMENT_GROUPS,
    SET_PRODUCT_REFERENCE_SOURCE,
} from "constants/action-types";
import { IStore } from "reducers";
import me from "../models/me";
import { getProductPath } from "helpers/product-path";

interface IOptions {
    forRef?: string;
    fromRef?: string;
}

export const setCurrentItem = (id: string, force = false, urlViewAs = null) => {
    return (dispatch, getState: () => IStore) => {
        const state = getState();
        const { scope } = state;
        const catalog = state.catalog.result.length ? state.catalog.entities.catalogs[state.catalog.result] : null;

        const item = state.entities.items && state.entities.items[id];
        const error = state.item.error;
        if (item && !force && !error) {
            dispatch(resetCurrentSelection());
            return dispatch({
                payload: item,
                type: ITEM_SET_CURRENT,
            });
        } else {
            return dispatch({
                [CALL_API]: {
                    endpoint: `/api/v3.0/item/${id}`,
                    query: {
                        __effective_date_gte: catalog?.delivery_window_start?.toString(),
                        __availability_start_date_gte: catalog?.delivery_window_start?.toString(),
                        __availability_end_date_lte: catalog?.delivery_window_end?.toString(),
                        __active_pricesheet: me._activePriceTemplate,
                        __company: urlViewAs ? urlViewAs : scope.viewAs?._id,
                        __advanced_pricing_currency_code: scope.brand?.getAdvancedPricingCurrencyCode(scope),
                        __fields: "__all",
                        __populate: ["__currency_breakdown", "__promotions", "__variant"],
                        __view_disabled: true,
                    },
                    records: {
                        items: {
                            create: createItemRecord,
                        },
                    },
                    schema: ItemSchema,
                    types: [ITEM_REQUEST, ITEM_SUCCESS, ITEM_FAILURE],
                },
            });
        }
    };
};

export const clearCurrentItem = () => {
    return (dispatch, getState: () => IStore) => {
        const state = getState();
        if (state.item?.referenceSource) {
            dispatch(clearProductReferenceSource());
        }
        dispatch({
            payload: undefined,
            type: ITEM_SET_CURRENT,
        });
    };
};

export const setProductReferenceSource = (value) => ({
    type: SET_PRODUCT_REFERENCE_SOURCE,
    payload: value,
});

export const clearProductReferenceSource = () => ({
    type: SET_PRODUCT_REFERENCE_SOURCE,
    payload: undefined,
});

export const redirectToProduct = (id: string, location: Location) => (dispatch) =>
    dispatch(
        push({
            pathname: getProductPath(id, location.pathname, navigator.userAgent),
            query: location.query,
        }),
    );

export const changeWarehouse = (warehouseId: string) => {
    return {
        payload: warehouseId,
        type: ITEM_WAREHOUSE_CHANGE,
    };
};

export const availability = (itemId: string, options: IOptions = {}) => {
    return (dispatch, getState) => {
        const viewAs = getState().scope.viewAs;

        const { catalog } = getState();
        const catalogScoped = !!window.location?.pathname?.indexOf("/linesheet/");

        const effectiveDateMs =
            catalogScoped && catalog.result.length
                ? catalog.entities.catalogs[catalog.result]?.get("delivery_window_start")
                : null;

        return dispatch(
            requestAsync(getItemAvailability(itemId, { ...options, company: viewAs && viewAs._id, effectiveDateMs })),
        )
            .then(checkForLogout)
            .then(({ status, transformed }) => {
                if (status >= 200 && status < 300 && transformed) {
                    const { warehouseResults, availabilityResults } = transformed;
                    dispatch({
                        payload: {
                            availabilities: availabilityResults,
                            warehouses: warehouseResults,
                        },
                        type: ITEM_WAREHOUSE_AVAILABILITY,
                    });
                } else if (status >= 500) {
                    dispatch(
                        snackbarAdd({
                            message: formatMessage("Oops! Something went wrong. Please try again"),
                        }),
                    );
                }
            });
    };
};

export const replenishments = (itemId: string, warehouseId: string) => {
    return (dispatch, getState) => {
        const viewAs = getState().scope.viewAs;
        return dispatch(requestAsync(getItemWarehouseReplenishments(itemId, warehouseId, viewAs && viewAs._id)))
            .then(checkForLogout)
            .then(({ transformed }) => {
                const { results } = transformed;
                dispatch({
                    meta: {
                        itemId,
                        warehouseId,
                    },
                    payload: results,
                    type: ITEM_WAREHOUSE_REPLENISHMENT_GROUPS,
                });
            });
    };
};

export const inventory = (itemId: string, warehouseId: string, date: moment.Moment, options: IOptions = {}) => {
    return (dispatch, getState) => {
        const { catalog } = getState();
        const catalogScoped = !!window.location?.pathname?.indexOf("/linesheet/");

        const effectiveDateMs =
            catalogScoped && catalog.result.length
                ? catalog.entities.catalogs[catalog.result]?.get("delivery_window_start")
                : null;

        const viewAs = getState().scope.viewAs;
        return dispatch(
            requestAsync(
                getInventoryForWarehouse(itemId, warehouseId, date, {
                    ...options,
                    company: viewAs && viewAs._id,
                    effectiveDateMs,
                }),
            ),
        )
            .then(checkForLogout)
            .then(({ status, transformed }) => {
                if (status >= 200 && status < 300 && transformed) {
                    const { results } = transformed;
                    dispatch({
                        meta: {
                            date,
                            itemId,
                            warehouseId,
                        },
                        payload: results,
                        type: ITEM_INVENTORY,
                    });
                }
            });
    };
};

export const variants = (options: { updateEntities?: boolean } = {}) => {
    return (dispatch, getState: () => IStore) => {
        const state = getState();
        const { scope } = state;
        const catalog = state.catalog.result.length ? state.catalog.entities.catalogs[state.catalog.result] : null;

        const item = state.item.current;
        if (!item) {
            return;
        }
        return dispatch(
            requestAsync(
                getItemVariants(item, {
                    effectiveDate: catalog?.delivery_window_start?.toString(),
                    availableFrom: catalog?.delivery_window_start?.toString(),
                    availableUntil: catalog?.delivery_window_end?.toString(),
                    styleNumber: item.style_number,
                    companyId: scope.viewAs?._id,
                    currencyCode: scope.brand?.getAdvancedPricingCurrencyCode(scope),
                    updateEntities: options?.updateEntities ?? true,
                }),
            ),
        )
            .then(checkForLogout)
            .then(({ transformed }) => {
                const { _result } = transformed;
                dispatch({
                    meta: {
                        item,
                    },
                    payload: _result,
                    type: ITEM_VARIENTS,
                });
            });
    };
};
